import React, { FC } from 'react';
import { PaymentElement } from '@stripe/react-stripe-js';
import { Alert, Field, TextInput } from '@kontentino/ui';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';

type Props = {
  clientSecret: string | null;
  holderName: string;
  billingEmail: string;
  onHolderNameChange: (value: string) => void;
  onBillingEmailChange: (value: string) => void;
  /**
   * Fires when the PaymentElement signals a change OR when one of the side
   * fields changes. Lets the parent clear any previously-shown payment
   * error so the user isn't staring at a stale message while editing.
   */
  onPaymentInputChange: () => void;
  errorMessage: string | null;
  holderNameError?: string;
  billingEmailError?: string;
};

const StripePaymentMethodFields: FC<Props> = ({
  clientSecret,
  holderName,
  billingEmail,
  onHolderNameChange,
  onBillingEmailChange,
  onPaymentInputChange,
  errorMessage,
  holderNameError,
  billingEmailError,
}) => {
  const { t } = useTranslation();

  return (
    <section className="tw-flex tw-flex-col tw-gap-4">
      <h3 className="tw-text-base tw-font-semibold tw-text-grayscale-90">
        {t('billing.stripe.cardLabel')}
      </h3>

      <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
        <Field.Group>
          <Field.Label required>{t('billing.stripe.holderName')}</Field.Label>
          <TextInput
            value={holderName}
            onChange={(e) => {
              onHolderNameChange(e.target.value);
              onPaymentInputChange();
            }}
            error={!!holderNameError}
            autoComplete="cc-name"
            data-cy="stripe-payment_holder-name"
          />
          {holderNameError && <Field.Error>{holderNameError}</Field.Error>}
        </Field.Group>

        <Field.Group>
          <Field.Label required>{t('billing.stripe.billingEmail')}</Field.Label>
          <TextInput
            type="email"
            value={billingEmail}
            onChange={(e) => {
              onBillingEmailChange(e.target.value);
              onPaymentInputChange();
            }}
            error={!!billingEmailError}
            autoComplete="email"
            data-cy="stripe-payment_billing-email"
          />
          {billingEmailError && <Field.Error>{billingEmailError}</Field.Error>}
        </Field.Group>
      </div>

      {errorMessage && (
        <Alert variant="danger" data-cy="stripe-payment_error">
          {errorMessage}
        </Alert>
      )}

      {!clientSecret ? (
        <div data-cy="setup-subscription_stripe-loading">
          <Skeleton height={48} className="tw-rounded" />
        </div>
      ) : (
        <div data-cy="setup-subscription_stripe-element">
          <PaymentElement onChange={() => onPaymentInputChange()} />
        </div>
      )}
    </section>
  );
};

export default StripePaymentMethodFields;
